import { useId, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { Field, Input } from "@/components/ui/input";
import { PAY_COPY } from "./copy";
import { saveDeclaration, type PayError } from "./pay-api";
import type { PayDeclarationInput, PayDeclarationView } from "./pay-types";
import { useIdempotency } from "./use-pay";

/**
 * The payer declaration: legal name, country, contact email.
 *
 * The service requires it before it will issue an address (`DECLARATION_REQUIRED`
 * if skipped) and freezes it once one exists (`DECLARATION_FROZEN` on a later
 * edit), so this component has three shapes and no fourth:
 *
 *   - **no declaration yet** — the form;
 *   - **declaration, not frozen** — the form, pre-filled with what the service
 *     returned, so a corrected typo is an edit rather than a re-entry;
 *   - **frozen** — read-only, with the lock explained. Attempting an edit after
 *     the address is issued is refused by the service; showing a form that can
 *     only fail would be worse than saying why it is closed.
 *
 * The client-side checks below (all three fields present, email roughly an
 * email, country two letters) exist so a payer is not made to wait for a round
 * trip to learn they left a field blank. **They grant nothing**: whatever
 * passes is still sent, and the service's own refusal — `VALIDATION_FAILED` or
 * any other code — is displayed verbatim with its support reference.
 */

/** Deliberately loose: one `@`, something either side, a dot in the domain. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COUNTRY_SHAPE = /^[A-Za-z]{2}$/;

export function DeclarationForm({
  token,
  declaration,
  onSaved,
}: {
  token: string;
  declaration: PayDeclarationView | null;
  onSaved: (declaration: PayDeclarationView) => void;
}) {
  const ids = useId();
  const [legalName, setLegalName] = useState(declaration?.legalName ?? "");
  const [country, setCountry] = useState(declaration?.country ?? "");
  const [contactEmail, setContactEmail] = useState(declaration?.contactEmail ?? "");
  const [errors, setErrors] = useState<Partial<Record<keyof PayDeclarationInput, string>>>({});
  const [refusal, setRefusal] = useState<PayError | null>(null);
  const [saving, setSaving] = useState(false);
  const { keyFor, settle } = useIdempotency("pay-declaration");

  if (declaration?.frozen) {
    return (
      <Card>
        <CardBody>
          <CardTitle>{PAY_COPY.declarationHeading}</CardTitle>
          <p className="mt-2 text-sm font-semibold text-ink">{PAY_COPY.declarationFrozenTitle}</p>
          <p className="mt-1 text-sm text-muted">{PAY_COPY.declarationFrozenNote}</p>
          <dl className="mt-3 flex flex-col gap-1.5 text-sm">
            <DeclarationRow label={PAY_COPY.declarationLegalNameLabel} value={declaration.legalName} />
            <DeclarationRow label={PAY_COPY.declarationCountryLabel} value={declaration.country} />
            <DeclarationRow label={PAY_COPY.declarationEmailLabel} value={declaration.contactEmail} />
          </dl>
        </CardBody>
      </Card>
    );
  }

  const body: PayDeclarationInput = { legalName: legalName.trim(), country: country.trim().toUpperCase(), contactEmail: contactEmail.trim() };

  function validate(): boolean {
    const next: Partial<Record<keyof PayDeclarationInput, string>> = {};
    if (!body.legalName) next.legalName = "Enter your legal name.";
    if (!COUNTRY_SHAPE.test(body.country)) next.country = "Use the two-letter country code, for example DE.";
    if (!EMAIL_SHAPE.test(body.contactEmail)) next.contactEmail = "Enter an email address the issuer can reach you at.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setRefusal(null);
    if (!validate()) return;

    setSaving(true);
    // One key for this attempt: pressing save again after a dropped connection
    // replays the same declaration rather than writing a second one.
    const result = await saveDeclaration(token, body, { idempotencyKey: keyFor(body) });
    setSaving(false);

    if (result.ok) {
      settle(true);
      onSaved(result.data);
      return;
    }
    // A refusal the service decided ends the attempt; a request that never
    // arrived does not, so its key stays for the retry.
    settle(result.status !== 0);
    setRefusal(result.error);
  }

  return (
    <Card>
      <CardBody>
        <CardTitle>{PAY_COPY.declarationHeading}</CardTitle>
        <p className="mt-2 text-sm text-muted">{PAY_COPY.declarationIntro}</p>

        <form className="mt-4 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <Field
            label={PAY_COPY.declarationLegalNameLabel}
            htmlFor={`${ids}-legal-name`}
            required
            error={errors.legalName}
          >
            <Input
              id={`${ids}-legal-name`}
              name="legalName"
              autoComplete="name"
              value={legalName}
              error={Boolean(errors.legalName)}
              placeholder={PAY_COPY.declarationLegalNamePlaceholder}
              onChange={(e) => setLegalName(e.target.value)}
            />
          </Field>

          <Field label={PAY_COPY.declarationCountryLabel} htmlFor={`${ids}-country`} required hint={PAY_COPY.declarationCountryHint} error={errors.country}>
            <Input
              id={`${ids}-country`}
              name="country"
              autoComplete="country"
              maxLength={2}
              value={country}
              error={Boolean(errors.country)}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
            />
          </Field>

          <Field
            label={PAY_COPY.declarationEmailLabel}
            htmlFor={`${ids}-email`}
            required
            hint={PAY_COPY.declarationEmailHint}
            error={errors.contactEmail}
          >
            <Input
              id={`${ids}-email`}
              name="contactEmail"
              type="email"
              autoComplete="email"
              value={contactEmail}
              error={Boolean(errors.contactEmail)}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </Field>

          {refusal && (
            <div role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">
              {refusal.reasonCode === "DECLARATION_FROZEN"
                ? PAY_COPY.declarationFrozenNote
                : refusal.reasonCode}
              <SupportDetails reference={refusal.supportReference} capability={refusal.capability} />
            </div>
          )}

          <div>
            <Button type="submit" disabled={saving}>
              {saving ? PAY_COPY.declarationSaving : PAY_COPY.declarationSubmit}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function DeclarationRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className="break-all font-semibold text-ink">{value}</dd>
    </div>
  );
}
